import React, { useState } from 'react';
import type { DeliveryOrder, DeliveryPlatform } from '../types';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import XCircleIcon from '../components/icons/XCircleIcon';
import LocationMarkerIcon from '../components/icons/LocationMarkerIcon';
import ClockIcon from '../components/icons/ClockIcon';

interface DeliveryIntegrationsPageProps {
  deliveryOrders: DeliveryOrder[];
  onAccept: (order: DeliveryOrder) => void;
  onReject: (order: DeliveryOrder) => void;
}

const DeliveryOrderCard: React.FC<{ order: DeliveryOrder, onAccept: () => void, onReject: () => void }> = ({ order, onAccept, onReject }) => {
    const platformColors = {
        'Uber Eats': 'bg-green-500',
        'DoorDash': 'bg-red-500'
    };

    const formatTime = (timestamp: number) => {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    return (
        <div className="bg-white rounded-lg shadow-md flex flex-col">
            <header className={`p-4 rounded-t-lg text-white ${platformColors[order.platform]}`}>
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold">{order.platform} Order</h3>
                    <span className="text-sm font-semibold">#{order.platformOrderId}</span>
                </div>
                <p className="text-sm">Customer: {order.customerName}</p>
            </header>
            <div className="p-4 space-y-3">
                 <div>
                    <div className="flex items-center text-sm text-gray-600">
                        <LocationMarkerIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="font-semibold">{order.deliveryAddress}</span>
                    </div>
                 </div>
                 <div>
                    <div className="flex items-center text-sm text-gray-600">
                        <ClockIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>Est. Delivery: <span className="font-semibold">{formatTime(order.estimatedDeliveryTime)}</span></span>
                    </div>
                </div>
            </div>
            <div className="border-t mx-4"></div>
            <ul className="p-4 flex-1">
                {order.items.map((item, index) => (
                    <li key={index} className="py-1">
                        <span className="font-semibold">{item.quantity}x</span> {item.name}
                        {item.specialInstructions && <p className="text-xs text-gray-500 pl-4"> - "{item.specialInstructions}"</p>}
                    </li>
                ))}
            </ul>
            <footer className="p-4 border-t">
                {order.status === 'new' ? (
                    <div className="flex space-x-4">
                        <button onClick={onReject} className="w-full bg-red-500 text-white font-bold py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center">
                            <XCircleIcon className="w-5 h-5 mr-2" /> Reject
                        </button>
                        <button onClick={onAccept} className="w-full bg-green-500 text-white font-bold py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center">
                           <CheckCircleIcon className="w-5 h-5 mr-2" /> Accept
                        </button>
                    </div>
                ) : (
                    <p className={`text-center font-bold capitalize ${order.status === 'accepted' ? 'text-green-600' : 'text-red-600'}`}>
                        Order {order.status}
                    </p>
                )}
            </footer>
        </div>
    );
};

const DeliveryIntegrationsPage: React.FC<DeliveryIntegrationsPageProps> = ({ deliveryOrders, onAccept, onReject }) => {
  const [activeTab, setActiveTab] = useState<DeliveryPlatform | 'All'>('All');
  
  const filteredOrders = activeTab === 'All' 
    ? deliveryOrders 
    : deliveryOrders.filter(o => o.platform === activeTab);

  const newOrders = filteredOrders.filter(o => o.status === 'new');

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">Delivery Integrations</h1>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                <button onClick={() => setActiveTab('All')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'All' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>All</button>
                <button onClick={() => setActiveTab('Uber Eats')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'Uber Eats' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Uber Eats</button>
                <button onClick={() => setActiveTab('DoorDash')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'DoorDash' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>DoorDash</button>
            </nav>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-700 mb-4">New Incoming Orders ({newOrders.length})</h2>
        
        {newOrders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {newOrders.map(order => (
                    <DeliveryOrderCard 
                        key={order.id} 
                        order={order} 
                        onAccept={() => onAccept(order)} 
                        onReject={() => onReject(order)}
                    />
                ))}
            </div>
        ) : (
            <div className="text-center py-10 bg-white rounded-lg shadow">
                <p className="text-gray-500">No new delivery orders at the moment.</p>
            </div>
        )}
      </main>
    </div>
  );
};

export default DeliveryIntegrationsPage;